/**
 * Centralized Z-Index Management
 *
 * This file defines the complete z-index hierarchy for the application.
 * All components should reference these constants to maintain proper layering.
 *
 * HIERARCHY OVERVIEW:
 * ==================
 * 1000-1500: Viewer Components (inside CroppedMeasurementsDialog)
 * 2000-2999: Application UI (sidebars, panels)
 * 8000-8999: Full-screen dialogs
 * 9000-9999: Global modals and notifications
 *
 * @module utils/zIndexLayers
 */

import appConfig from '../../appConfig';
import { log as debugLog, warn as debugWarn } from '@/utils/debugLogger';

export const Z_INDEX = {
  // ============================================
  // VIEWER LAYER (1000-1500)
  // Components inside the Potree measurements viewer
  // ============================================

  /**
   * Base viewer layer - rendering canvas and background
   * @type {number}
   */
  VIEWER_BASE: 1000,

  /**
   * Viewer toolbars - ViewToolbar, ClippingToolbar
   * Fixed position navigation and clipping controls
   * @type {number}
   */
  VIEWER_TOOLBARS: 1100,

  /**
   * Draggable panels - Measurements Panel
   * User-repositionable measurement list panel
   * @type {number}
   */
  VIEWER_PANELS: 1200,

  /**
   * Settings dialogs - Material, Filters, Clipping dialogs
   * Modal dialogs for viewer configuration
   * @type {number}
   */
  VIEWER_DIALOGS: 1300,

  /**
   * Floating panels - Corrosion Analysis Panel
   * Draggable analysis and data display panels
   * @type {number}
   */
  VIEWER_FLOATING: 1310,

  /**
   * Overlay elements - Loading overlays, modal backdrops
   * Full-screen blocking overlays within viewer
   * @type {number}
   */
  VIEWER_OVERLAYS: 1400,

  /**
   * Cylinder mode instructions - Step-by-step guide card
   * Modal instructions for cylinder fitting mode
   * @type {number}
   */
  VIEWER_INSTRUCTIONS: 1450,

  /**
   * Mouse position info - Real-time coordinate display
   * Always-visible 3D coordinate tracker
   * @type {number}
   */
  VIEWER_MOUSE_INFO: 1500,

  // ============================================
  // APPLICATION LAYER (2000-2999)
  // Global application UI elements
  // ============================================

  /**
   * Left navigation sidebar - Main app navigation
   * Contains tabs for search, tags, scans, etc.
   * @type {number}
   */
  APP_SIDEBAR: 2000,

  /**
   * Display settings panel - Viewer display configuration
   * Slides over sidebar, controls visibility and appearance
   * @type {number}
   */
  APP_DISPLAY_SETTINGS: 2100,

  /**
   * Top navigation bar - Global app header
   * Contains user menu, notifications, etc.
   * @type {number}
   */
  APP_TOP_NAV: 2200,

  // ============================================
  // DIALOG LAYER (8000-8999)
  // Full-screen or large modal dialogs
  // ============================================

  /**
   * Cropped Measurements Dialog - Full-screen point cloud viewer
   * Contains entire PotreeMeasurementsApp component tree
   * @type {number}
   */
  APP_CROPPED_DIALOG: 8000,

  // ============================================
  // MODAL LAYER (9000-9999)
  // Vuetify modals and global notifications
  // ============================================

  /**
   * Global modals - Vuetify v-dialog default range
   * Confirmation dialogs, alerts, etc.
   * @type {number}
   */
  APP_MODAL: 9000,

  /**
   * Toast notifications - Success/error messages
   * Temporary notification popups
   * @type {number}
   */
  APP_TOAST: 9500
};

/**
 * Helper function to get z-index with optional offset
 * Useful for temporarily boosting z-index (e.g., while dragging)
 *
 * @param {number} baseZIndex - Base z-index from Z_INDEX constants
 * @param {number} offset - Optional offset to add (default: 0)
 * @returns {number} Calculated z-index value
 *
 * @example
 * // Get base panel z-index
 * const panelZ = getZIndex(Z_INDEX.VIEWER_PANELS);
 *
 * @example
 * // Boost panel z-index while dragging
 * const draggingZ = getZIndex(Z_INDEX.VIEWER_PANELS, 50);
 */
export function getZIndex(baseZIndex, offset = 0) {
  return baseZIndex + offset;
}

/**
 * Validate that z-index values don't conflict
 * Checks if a z-index falls within the correct layer range
 *
 * @param {number} zIndex - The z-index to validate
 * @param {string} expectedLayer - Expected layer name ('viewer', 'app', 'dialog', 'modal')
 * @returns {boolean} True if z-index is in correct range
 *
 * @example
 * validateZIndex(1250, 'viewer'); // true - 1250 is in viewer range (1000-1500)
 * validateZIndex(3000, 'viewer'); // false - 3000 is outside viewer range
 */
export function validateZIndex(zIndex, expectedLayer) {
  const ranges = {
    viewer: { min: 1000, max: 1599 },
    app: { min: 2000, max: 2999 },
    dialog: { min: 8000, max: 8999 },
    modal: { min: 9000, max: 9999 }
  };

  const range = ranges[expectedLayer];
  if (!range) {
    debugWarn('zIndexLayers', `Unknown layer: ${expectedLayer}`);
    return false;
  }

  const isValid = zIndex >= range.min && zIndex <= range.max;

  if (!isValid) {
    debugWarn('zIndexLayers', `Z-index ${zIndex} is outside ${expectedLayer} layer range (${range.min}-${range.max})`);
  }

  return isValid;
}

/**
 * Debug helper: Log current z-index hierarchy
 * Useful for troubleshooting layering issues
 */
export function logZIndexHierarchy() {
  // Only log if debug mode is enabled
  if (!appConfig.debugMode) return;

  debugLog('zIndexLayers', '📊 Z-Index Hierarchy');

  debugLog('zIndexLayers', 'Viewer Layer (1000-1500)');
  debugLog('zIndexLayers', '  VIEWER_BASE:', Z_INDEX.VIEWER_BASE);
  debugLog('zIndexLayers', '  VIEWER_TOOLBARS:', Z_INDEX.VIEWER_TOOLBARS);
  debugLog('zIndexLayers', '  VIEWER_PANELS:', Z_INDEX.VIEWER_PANELS);
  debugLog('zIndexLayers', '  VIEWER_DIALOGS:', Z_INDEX.VIEWER_DIALOGS);
  debugLog('zIndexLayers', '  VIEWER_FLOATING:', Z_INDEX.VIEWER_FLOATING);
  debugLog('zIndexLayers', '  VIEWER_OVERLAYS:', Z_INDEX.VIEWER_OVERLAYS);
  debugLog('zIndexLayers', '  VIEWER_INSTRUCTIONS:', Z_INDEX.VIEWER_INSTRUCTIONS);
  debugLog('zIndexLayers', '  VIEWER_MOUSE_INFO:', Z_INDEX.VIEWER_MOUSE_INFO);

  debugLog('zIndexLayers', 'Application Layer (2000-2999)');
  debugLog('zIndexLayers', '  APP_SIDEBAR:', Z_INDEX.APP_SIDEBAR);
  debugLog('zIndexLayers', '  APP_DISPLAY_SETTINGS:', Z_INDEX.APP_DISPLAY_SETTINGS);
  debugLog('zIndexLayers', '  APP_TOP_NAV:', Z_INDEX.APP_TOP_NAV);

  debugLog('zIndexLayers', 'Dialog Layer (8000-8999)');
  debugLog('zIndexLayers', '  APP_CROPPED_DIALOG:', Z_INDEX.APP_CROPPED_DIALOG);

  debugLog('zIndexLayers', 'Modal Layer (9000-9999)');
  debugLog('zIndexLayers', '  APP_MODAL:', Z_INDEX.APP_MODAL);
  debugLog('zIndexLayers', '  APP_TOAST:', Z_INDEX.APP_TOAST);
}

// Export default for convenience
export default Z_INDEX;
